export const langTypes = {
    ZH_TW: 'zh-TW',
    ZH_CN: 'zh-CN',
    ZH_HK: 'zh-HK',
    EN_US: 'en-US',
    JA_JP: 'ja',
    PT_BR: 'pt-BR',
    ES_419: 'es-419',
    TH_TH: 'th-TH'
};

export const promoLangOption = {
    [langTypes.ZH_TW]: 'label.game_lang.zh-TW',
    [langTypes.ZH_CN]: 'label.game_lang.zh-CN',
    [langTypes.EN_US]: 'label.game_lang.en-US',
    [langTypes.JA_JP]: 'label.game_lang.ja',
    [langTypes.PT_BR]: 'label.game_lang.pt-BR',
    [langTypes.ES_419]: 'label.game_lang.es-419',
    [langTypes.TH_TH]: 'label.game_lang.th-TH'
};

export const promoLangOptions = [
    { value: langTypes.ZH_TW, label: 'label.game_lang.zh-TW' },
    { value: langTypes.ZH_CN, label: 'label.game_lang.zh-CN' },
    { value: langTypes.EN_US, label: 'label.game_lang.en-US' },
    { value: langTypes.JA_JP, label: 'label.game_lang.ja' },
    { value: langTypes.PT_BR, label: 'label.game_lang.pt-BR' },
    { value: langTypes.ES_419, label: 'label.game_lang.es-419' },
    { value: langTypes.TH_TH, label: 'label.game_lang.th-TH' }
];

/**
 * 編輯器介面語言（sponsor 後台看到的面板文字）。
 *
 * 與活動語言 `promoLangOptions`（7 語，玩家看到的）是兩條不同的軌：這條只管
 * **誰在看面板**，那條管 **誰在玩遊戲**。取值分別見 hooks/useAdminLanguage 與
 * hooks/usePromoLanguage。
 *
 * 加語言時要一起看的東西：`src/locale/editor/<lang>.json` 要存在（i18n.js 已經
 * 一律載入 7 語的檔案），以及內建遊戲 island 的 `gameConfigMeta` 標籤語言包
 * （#86eyjzb3h）——面板標籤是照這份清單備齊的，少一種就會靜靜退回 zh-TW。
 */
export const adminLanguages = [langTypes.ZH_TW, langTypes.EN_US, langTypes.JA_JP];

/**
 * Map language code to locale code for S3 directory names
 * According to spec.md FR-003: Use full language codes as S3 directory names
 * @param {string} language - Full language code (e.g., 'en-US', 'zh-TW', 'ja', 'th-TH')
 * @returns {string} - Locale code for directory name
 */
export const mapLanguageToLocale = language => {
    // Special case: th-TH should be mapped to th for directory names (backward compatibility)
    if (language === 'th-TH') {
        return 'th';
    }

    // Return the language code as-is, or fallback to zh-TW
    return language || 'zh-TW';
};

/**
 * Get localized game URL by replacing locale directory
 * New format: [baseUrl]/[locale]/[game-name].html
 * Example: https://assets.fevercdn.com/.../en-US/highway-dodge.html
 * @param {string} baseUrl - Base CDN URL (e.g., 'https://assets.fevercdn.com/.../zh-TW/highway-dodge.html')
 * @param {string} locale - Target locale code (e.g., 'en-US', 'ja', 'es-419')
 * @returns {string} - Localized game URL
 */
export const getLocalizedGameUrl = (baseUrl, locale) => {
    // List of supported language directories (from spec.md FR-006)
    const supportedLocales = ['en-US', 'zh-TW', 'zh-CN', 'ja', 'es-419', 'pt-BR', 'th', 'th-TH'];

    // Try to find and replace existing locale directory in URL
    for (const existingLocale of supportedLocales) {
        if (baseUrl.includes(`/${existingLocale}/`)) {
            // Use string replacement instead of regex to avoid ReDoS vulnerability
            // Since existingLocale is from a controlled list, we can safely use split/join
            return baseUrl.split(`/${existingLocale}/`).join(`/${locale}/`);
        }
    }

    // If no existing locale directory found, insert locale before filename
    // This handles legacy URLs without locale directories
    const lastSlashIndex = baseUrl.lastIndexOf('/');
    if (lastSlashIndex !== -1) {
        const basePath = baseUrl.substring(0, lastSlashIndex);
        const filename = baseUrl.substring(lastSlashIndex + 1);
        return `${basePath}/${locale}/${filename}`;
    }

    // Fallback: return original URL if we can't determine structure
    return baseUrl;
};

/**
 * 將瀏覽器的 navigator.language 轉換成 langTypes 中對應的語言代碼
 * @param {string} navigatorLang - 瀏覽器的 navigator.language 值（例如：'zh-TW', 'en', 'ja-JP'）
 * @param {string} defaultLang - 預設語言，默認為 'en'
 * @returns {string} - langTypes 中對應的語言代碼
 */
export const convertNavigatorLanguage = (navigatorLang, defaultLang = langTypes.EN_US) => {
    if (!navigatorLang) {
        return defaultLang;
    }

    // 正規化：轉為小寫以便比較
    const normalizedLang = navigatorLang.toLowerCase();

    // 直接匹配 langTypes 中的值
    const directMatch = Object.values(langTypes).find(
        lang => lang.toLowerCase() === normalizedLang
    );
    if (directMatch) return directMatch;

    // 語言代碼映射（處理不同的格式變化）
    const languageMapping = {
        'zh-tw': langTypes.ZH_TW,
        'zh-cn': langTypes.ZH_CN,
        'zh-hk': langTypes.ZH_HK,
        en: langTypes.EN_US,
        'en-us': langTypes.EN_US,
        'en-gb': langTypes.EN_US, // 英國英語也映射到美國英語
        ja: langTypes.JA_JP,
        'ja-jp': langTypes.JA_JP,
        pt: langTypes.PT_BR,
        'pt-br': langTypes.PT_BR,
        es: langTypes.ES_419,
        'es-419': langTypes.ES_419,
        'es-es': langTypes.ES_419, // 西班牙西語也映射到拉丁美洲西語
        th: langTypes.TH_TH,
        'th-th': langTypes.TH_TH
    };

    const mappedLang = languageMapping[normalizedLang];
    if (mappedLang) return mappedLang;

    // 沒有匹配時返回預設語言
    return defaultLang;
};
